import { useEffect, useState } from "react";
import PageMeta from "../../components/common/PageMeta";
// import ChurnRateChart from "../../components/saas/ChurnRateChart";
// import FunnelChart from "../../components/saas/FunnelChart";
import FilingBreakdown from "../../components/saas/FilingBreakdown";
// import GrowthChart from "../../components/saas/GrowthChart";
import FilingTable from "../../components/saas/FilingTable";
import SaasMetrics from "../../components/saas/SaasMetrics";
import { CardSkeleton } from "../../components/ui/skeleton";
import { fetchUsageSummary, UsageSummaryResponse } from "../../lib/api/tenant";

export default function Saas() {
  const [usageSummary, setUsageSummary] = useState<UsageSummaryResponse | null>(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState<string | null>(null);

  useEffect(() => {
    const loadUsageSummary = async () => {
      try {
        setUsageLoading(true);
        setUsageError(null);
        const summary = await fetchUsageSummary({ group_by: "event_type" });
        setUsageSummary(summary);
      } catch (err: any) {
        setUsageError(err.message || "Failed to load usage data");
      } finally {
        setUsageLoading(false);
      }
    };

    loadUsageSummary();
  }, []);

  // Calculate total tokens used across all event types
  const calculateTotalTokens = (): number => {
    if (!usageSummary?.summary) return 0;
    return Object.values(usageSummary.summary).reduce(
      (total, item) => total + (item.total_tokens || 0),
      0
    );
  };

  return (
    <>
      <PageMeta
        title="React.js SaaS Dashboard | TailAdmin - React.js Admin Dashboard Template"
        description="This is React.js SaaS Dashboard page for TailAdmin - React.js Tailwind CSS Admin Dashboard Template"
      />
      <div className="space-y-5 sm:space-y-6">
        {/* Dashboard header with Create Filing CTA */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white/90">Dashboard</h2>
          </div>
          <a
            href="/filings/new"
            className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-600 active:bg-brand-700 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Create Filing
          </a>
        </div>

        <SaasMetrics />

        {/* Table */}
        <FilingTable />

        <FilingBreakdown />

        {/* Usage This Month Section */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
              Usage This Month
            </h3>
          </div>

          {usageLoading ? (
            <CardSkeleton count={3} />
          ) : usageError ? (
            <div className="rounded-lg bg-error-50 p-4 dark:bg-error-500/15">
              <p className="text-sm font-medium text-error-700 dark:text-error-400">
                {usageError}
              </p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {/* Pre-plugging Plans */}
              <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Pre-plugging Plans</span>
                    <h4 className="mt-2 text-2xl font-bold text-gray-800 dark:text-white">
                      {usageSummary?.summary?.plan_generated?.count || 0}
                    </h4>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 dark:bg-brand-500/10">
                    <svg className="h-6 w-6 text-brand-600 dark:text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* AI Messages */}
              <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">AI Messages</span>
                    <h4 className="mt-2 text-2xl font-bold text-gray-800 dark:text-white">
                      {usageSummary?.summary?.ai_chat_message?.count || 0}
                    </h4>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-info-50 dark:bg-info-500/10">
                    <svg className="h-6 w-6 text-info-600 dark:text-info-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Total Tokens Used */}
              <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Total Tokens Used</span>
                    <h4 className="mt-2 text-2xl font-bold text-gray-800 dark:text-white">
                      {calculateTotalTokens().toLocaleString()}
                    </h4>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-warning-50 dark:bg-warning-500/10">
                    <svg className="h-6 w-6 text-warning-600 dark:text-warning-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
